import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Company, CreateCompanyRequest } from "../types/company";
import { ApiResponse, CompanyApiResponse } from "../types/auth";
import { ApplicationWithCompany } from "../types/application";
import { FaFilter } from "react-icons/fa";
import CompanyTemplateModal from "../components/CompanyTemplateModal";

const CompaniesPage: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [applications, setApplications] = useState<ApplicationWithCompany[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);

  // フィルター用のstate
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedInterestLevels, setSelectedInterestLevels] = useState<
    number[]
  >([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";
  const navigate = useNavigate();

  // チェックボックス選択処理
  const handleCheckboxChange = (companyId: number) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    );
  };

  // 比較ページへ遷移
  const handleCompare = () => {
    if (selectedCompanyIds.length < 2) {
      alert("比較するには2社以上選択してください");
      return;
    }
    const ids = selectedCompanyIds.join(",");
    navigate(`/compare-companies?ids=${ids}`);
  };

  // 企業一覧を取得
  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/companies`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data: ApiResponse<CompanyApiResponse> | any =
          await response.json();

        // 柔軟に受け付ける: data.data が配列の場合や { companies: [...] } の場合に対応
        const payload = data.data;
        let companiesPayload: Company[] = [];

        if (Array.isArray(payload)) {
          companiesPayload = payload;
        } else if (payload && Array.isArray(payload.companies)) {
          companiesPayload = payload.companies;
        } else if (payload && payload.company) {
          companiesPayload = [payload.company];
        }

        if (data.success && companiesPayload.length > 0) {
          setCompanies(companiesPayload);
        } else {
          setCompanies([]);
        }
      } else {
        setError("企業一覧の取得に失敗しました");
      }
    } catch (error) {
      setError("企業一覧の取得中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  // 応募状況一覧を取得
  const fetchApplications = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/applications`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data: ApiResponse<ApplicationWithCompany[]> =
          await response.json();
        if (data.success && Array.isArray(data.data)) {
          setApplications(data.data);
        } else {
          setApplications([]);
        }
      }
    } catch (error) {
      // エラーハンドリング
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchCompanies(), fetchApplications()]);
    };
    fetchData();
  }, [fetchCompanies, fetchApplications]);

  // 企業追加
  const handleAddCompany = async (companyData: CreateCompanyRequest) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(companyData),
      });

      if (response.ok) {
        setShowForm(false);
        fetchCompanies(); // 一覧を再取得
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "企業の登録に失敗しました");
      }
    } catch (error) {
      setError("企業の登録中にエラーが発生しました");
    }
  };

  // 企業編集
  const handleEditCompany = async (companyData: CreateCompanyRequest) => {
    if (!editingCompany) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/companies/${editingCompany.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(companyData),
        }
      );

      if (response.ok) {
        setShowEditForm(false);
        setEditingCompany(null);
        fetchCompanies(); // 一覧を再取得
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "企業の更新に失敗しました");
      }
    } catch (error) {
      setError("企業の更新中にエラーが発生しました");
    }
  };

  // ステータスラベルを取得
  const getStatusLabel = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      applied: "応募済み",
      document_screening: "書類選考中",
      interview_scheduled: "面接予定",
      final_interview: "最終面接",
      offer: "内定",
      rejected: "不合格",
      declined: "辞退",
    };
    return statusMap[status] || status;
  };

  // ステータスの色を取得
  const getStatusColor = (status: string): string => {
    const colorMap: { [key: string]: string } = {
      applied: "bg-blue-100 text-blue-800",
      document_screening: "bg-yellow-100 text-yellow-800",
      interview_scheduled: "bg-purple-100 text-purple-800",
      final_interview: "bg-orange-100 text-orange-800",
      offer: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      declined: "bg-gray-100 text-gray-800",
    };
    return colorMap[status] || "bg-gray-100 text-gray-800";
  };

  // 企業IDから応募状況を取得
  const getApplicationByCompanyId = (
    companyId: number
  ): ApplicationWithCompany | undefined => {
    return applications.find((app) => app.company_id === companyId);
  };

  // ユニークな業界リストを取得
  const uniqueIndustries = Array.from(
    new Set(
      companies
        .map((c) => c.industry)
        .filter((industry): industry is string => !!industry)
    )
  ).sort();

  // ステータスリスト（日本語ラベル付き）
  const statusOptions = [
    { value: "not_applied", label: "未登録" },
    { value: "applied", label: "応募済み" },
    { value: "document_screening", label: "書類選考中" },
    { value: "interview_scheduled", label: "面接予定" },
    { value: "final_interview", label: "最終面接" },
    { value: "offer", label: "内定" },
    { value: "rejected", label: "不合格" },
    { value: "declined", label: "辞退" },
  ];

  // フィルター適用
  const filteredCompanies = companies.filter((company) => {
    // 業界フィルター
    if (selectedIndustries.length > 0) {
      if (!company.industry || !selectedIndustries.includes(company.industry)) {
        return false;
      }
    }

    // 志望度フィルター
    if (selectedInterestLevels.length > 0) {
      if (
        !company.interest_level ||
        !selectedInterestLevels.includes(company.interest_level)
      ) {
        return false;
      }
    }

    // 応募状況フィルター
    if (selectedStatuses.length > 0) {
      const application = getApplicationByCompanyId(company.id);
      const currentStatus = application ? application.status : "not_applied";
      if (!selectedStatuses.includes(currentStatus)) {
        return false;
      }
    }

    return true;
  });

  // 全選択/全解除
  const isAllSelected =
    filteredCompanies.length > 0 &&
    filteredCompanies.every((c) => selectedCompanyIds.includes(c.id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCompanyIds([]);
    } else {
      setSelectedCompanyIds(filteredCompanies.map((c) => c.id));
    }
  };

  // フィルターをクリア
  const clearFilters = () => {
    setSelectedIndustries([]);
    setSelectedInterestLevels([]);
    setSelectedStatuses([]);
  };

  // フィルターが適用されているかチェック
  const hasActiveFilters =
    selectedIndustries.length > 0 ||
    selectedInterestLevels.length > 0 ||
    selectedStatuses.length > 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 text-gray-600">
        <div className="loading-spinner"></div>
        <span>読み込み中...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="bg-transparent border-none text-red-700 text-xl cursor-pointer px-2"
          >
            ×
          </button>
        </div>
      )}

      {/* 企業比較機能の説明 */}
      <div className="mb-3 bg-blue-50 border-l-4 border-blue-500 text-gray-700 p-3 rounded text-sm">
        💡 <strong>企業比較機能</strong>:
        チェックボックスで企業を選択し、「比較する」ボタンを押すと複数企業を並べて比較できます
      </div>

      {/* ヘッダー: ボタンとフィルター */}
      <div className="bg-white rounded-lg p-3 mb-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {/* 企業追加ボタン */}
          <button
            className="bg-gradient-to-br from-blue-500 to-green-600 text-white border-none py-2 px-4 rounded-lg text-base font-medium cursor-pointer transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
            onClick={() => setShowForm(true)}
          >
            + 企業を追加
          </button>

          {/* テンプレート呼び出しボタン */}
          <button
            className="bg-gradient-to-br from-indigo-500 to-blue-600 text-white border-none py-2 px-4 rounded-lg text-base font-medium cursor-pointer transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
            onClick={() => setShowTemplateModal(true)}
          >
            テンプレートから登録
          </button>

          {/* 比較ボタン */}
          {selectedCompanyIds.length > 0 && (
            <button
              onClick={handleCompare}
              className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-none py-2 px-4 rounded-lg text-base font-medium cursor-pointer transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              比較する ({selectedCompanyIds.length}社選択中)
            </button>
          )}

          {/* フィルターボタン */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all ${
              showFilters
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <span>
              <FaFilter />
            </span>
            <span>フィルター</span>
            {hasActiveFilters && (
              <span className="bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold">
                {selectedIndustries.length +
                  selectedInterestLevels.length +
                  selectedStatuses.length}
              </span>
            )}
          </button>

          {/* 選択中の業界タグ */}
          {selectedIndustries.map((industry) => (
            <span
              key={industry}
              className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
            >
              業界: {industry}
              <button
                onClick={() =>
                  setSelectedIndustries(
                    selectedIndustries.filter((i) => i !== industry)
                  )
                }
                className="bg-transparent border-none text-blue-800 hover:text-blue-900 cursor-pointer text-lg leading-none"
              >
                ×
              </button>
            </span>
          ))}

          {/* 選択中の志望度タグ */}
          {selectedInterestLevels.map((level) => (
            <span
              key={level}
              className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
            >
              志望度: {"★".repeat(level) + "☆".repeat(5 - level)}
              <button
                onClick={() =>
                  setSelectedInterestLevels(
                    selectedInterestLevels.filter((l) => l !== level)
                  )
                }
                className="bg-transparent border-none text-purple-800 hover:text-purple-900 cursor-pointer text-lg leading-none"
              >
                ×
              </button>
            </span>
          ))}

          {/* 選択中の応募状況タグ */}
          {selectedStatuses.map((status) => (
            <span
              key={status}
              className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
            >
              {statusOptions.find((s) => s.value === status)?.label || status}
              <button
                onClick={() =>
                  setSelectedStatuses(
                    selectedStatuses.filter((s) => s !== status)
                  )
                }
                className="bg-transparent border-none text-green-800 hover:text-green-900 cursor-pointer text-lg leading-none"
              >
                ×
              </button>
            </span>
          ))}

          {/* フィルタークリアボタン */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-red-600 transition-colors px-3 py-1"
            >
              すべてクリア
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <CompanyForm
          onSubmit={handleAddCompany}
          onCancel={() => setShowForm(false)}
        />
      )}

      {showEditForm && editingCompany && (
        <CompanyForm
          onSubmit={handleEditCompany}
          onCancel={() => {
            setShowEditForm(false);
            setEditingCompany(null);
          }}
          initialData={editingCompany}
          isEditMode={true}
        />
      )}

      {/* フィルターパネル */}
      {showFilters && (
        <div className="mb-4 bg-white rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 業界フィルター */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">業界</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {uniqueIndustries.length === 0 ? (
                  <p className="text-sm text-gray-400">業界データなし</p>
                ) : (
                  uniqueIndustries.map((industry) => (
                    <label
                      key={industry}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIndustries.includes(industry)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIndustries([
                              ...selectedIndustries,
                              industry,
                            ]);
                          } else {
                            setSelectedIndustries(
                              selectedIndustries.filter((i) => i !== industry)
                            );
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">{industry}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* 志望度フィルター */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">志望度</h3>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((level) => (
                  <label
                    key={level}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedInterestLevels.includes(level)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInterestLevels([
                            ...selectedInterestLevels,
                            level,
                          ]);
                        } else {
                          setSelectedInterestLevels(
                            selectedInterestLevels.filter((l) => l !== level)
                          );
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">
                      {"★".repeat(level) + "☆".repeat(5 - level)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* 応募状況フィルター */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">応募状況</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {statusOptions.map((status) => (
                  <label
                    key={status.value}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStatuses([
                            ...selectedStatuses,
                            status.value,
                          ]);
                        } else {
                          setSelectedStatuses(
                            selectedStatuses.filter((s) => s !== status.value)
                          );
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">
                      {status.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* フィルター結果の表示 */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                {filteredCompanies.length} 件 / {companies.length}{" "}
                件の企業を表示中
              </p>
            </div>
          )}
        </div>
      )}

      {companies.length === 0 ? (
        <div className="text-center py-16 px-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <h3 className="text-gray-600 mb-2">企業が登録されていません</h3>
          <p className="text-gray-400">
            「企業を追加」ボタンから企業情報を登録してください。
          </p>
        </div>
      ) : (
        <>
          {/* モバイル用カードビュー */}
          <div className="md:hidden space-y-4 mt-4">
            {filteredCompanies.length === 0 ? (
              <div className="text-center p-8 text-gray-500 bg-gray-50 rounded-xl">
                {hasActiveFilters
                  ? "フィルター条件に一致する企業が見つかりませんでした"
                  : "企業が登録されていません"}
              </div>
            ) : (
              filteredCompanies.map((company) => {
                const application = getApplicationByCompanyId(company.id);
                return (
                  <div
                    key={company.id}
                    className={`bg-white rounded-lg p-4 shadow-sm border-2 transition-all ${
                      selectedCompanyIds.includes(company.id)
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <input
                        type="checkbox"
                        checked={selectedCompanyIds.includes(company.id)}
                        onChange={() => handleCheckboxChange(company.id)}
                        className="w-5 h-5 mt-1 cursor-pointer flex-shrink-0"
                        style={{ minWidth: "20px", minHeight: "20px" }}
                      />
                      <div className="flex-1">
                        <Link
                          to={`/companies/${company.id}`}
                          className="text-lg font-semibold text-blue-600 hover:underline block mb-2"
                        >
                          {company.name}
                        </Link>
                        {selectedCompanyIds.includes(company.id) && (
                          <span className="inline-block bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium mb-2">
                            選択中
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      {company.industry && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 min-w-[70px]">
                            業界:
                          </span>
                          <span className="text-gray-800">
                            {company.industry}
                          </span>
                        </div>
                      )}

                      {company.interest_level && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 min-w-[70px]">
                            志望度:
                          </span>
                          <span className="text-gray-800">
                            {"★".repeat(company.interest_level) +
                              "☆".repeat(5 - company.interest_level)}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 min-w-[70px]">
                          応募状況:
                        </span>
                        {application ? (
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              application.status
                            )}`}
                          >
                            {getStatusLabel(application.status)}
                          </span>
                        ) : (
                          <span className="text-gray-400">未登録</span>
                        )}
                      </div>

                      {/* リンクボタン */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {company.website && (
                          <a
                            href={company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-xs font-medium hover:bg-blue-100 transition-colors"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                            Webサイト
                          </a>
                        )}
                        {company.mypage_url && (
                          <a
                            href={company.mypage_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded-md text-xs font-medium hover:bg-green-100 transition-colors"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                            マイページ
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* PC用テーブルビュー */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-[1200px] w-full table-fixed border-collapse bg-white mt-8 shadow-sm rounded-xl overflow-hidden">
              <thead>
                <tr>
                  <th className="w-14 px-3 py-4 border-b border-gray-200 text-left text-base bg-gray-100 text-gray-800 font-semibold">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>
                  <th className="w-72 p-4 border-b border-gray-200 text-left text-base bg-gray-100 text-gray-800 font-semibold">
                    企業名
                  </th>
                  <th className="p-4 border-b border-gray-200 text-left text-base bg-gray-100 text-gray-800 font-semibold">
                    業界
                  </th>
                  <th className="p-4 border-b border-gray-200 text-left text-base bg-gray-100 text-gray-800 font-semibold">
                    志望度
                  </th>
                  <th className="p-4 border-b border-gray-200 text-left text-base bg-gray-100 text-gray-800 font-semibold">
                    応募状況
                  </th>
                  <th className="p-4 border-b border-gray-200 text-left text-base bg-gray-100 text-gray-800 font-semibold">
                    ウェブサイト
                  </th>
                  <th className="p-4 border-b border-gray-200 text-left text-base bg-gray-100 text-gray-800 font-semibold">
                    マイページURL
                  </th>
                  <th className="p-4 border-b border-gray-200 text-left text-base bg-gray-100 text-gray-800 font-semibold">
                    マイページID
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-gray-500">
                      {hasActiveFilters
                        ? "フィルター条件に一致する企業が見つかりませんでした"
                        : "企業が登録されていません"}
                    </td>
                  </tr>
                ) : (
                  filteredCompanies.map((company) => {
                    const application = getApplicationByCompanyId(company.id);
                    return (
                      <tr
                        key={company.id}
                        className={`transition-colors duration-200 ${
                          selectedCompanyIds.includes(company.id)
                            ? "bg-purple-50 border-l-4 border-purple-500"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="w-14 px-3 py-4 border-b border-gray-200">
                          <input
                            type="checkbox"
                            checked={selectedCompanyIds.includes(company.id)}
                            onChange={() => handleCheckboxChange(company.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className="p-4 border-b border-gray-200">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/companies/${company.id}`}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              {company.name}
                            </Link>
                            {selectedCompanyIds.includes(company.id) && (
                              <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                                選択中
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 border-b border-gray-200">
                          {company.industry || "-"}
                        </td>
                        <td className="p-4 border-b border-gray-200">
                          {company.interest_level
                            ? "★".repeat(company.interest_level) +
                              "☆".repeat(5 - company.interest_level)
                            : "-"}
                        </td>
                        <td className="p-4 border-b border-gray-200 whitespace-nowrap">
                          {application ? (
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                                application.status
                              )}`}
                            >
                              {getStatusLabel(application.status)}
                            </span>
                          ) : (
                            <span className="text-gray-400">未登録</span>
                          )}
                        </td>
                        <td className="p-4 border-b border-gray-200">
                          {company.website ? (
                            <a
                              href={company.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              開く
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-4 border-b border-gray-200">
                          {company.mypage_url ? (
                            <a
                              href={company.mypage_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded-md text-sm font-medium hover:bg-green-100 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              開く
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-4 border-b border-gray-200">
                          {company.mypage_id || "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* 企業テンプレートモーダル */}
      <CompanyTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSuccess={() => {
          fetchCompanies();
          setShowTemplateModal(false);
        }}
      />
    </div>
  );
};

// 企業登録・編集フォームコンポーネント
interface CompanyFormProps {
  onSubmit: (data: CreateCompanyRequest) => void;
  onCancel: () => void;
  initialData?: Company;
  isEditMode?: boolean;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState<CreateCompanyRequest>({
    name: initialData?.name || "",
    industry: initialData?.industry || "",
    website: initialData?.website || "",
    mypage_url: initialData?.mypage_url || "",
    mypage_id: initialData?.mypage_id || "",
    mypage_password: "",
    interest_level: initialData?.interest_level || undefined,
    salary_min: initialData?.salary_min || undefined,
    salary_max: initialData?.salary_max || undefined,
    salary_ave: initialData?.salary_ave || undefined,
    holidays: initialData?.holidays || undefined,
    employee_count: initialData?.employee_count || undefined,
    benefits: initialData?.benefits || "",
    location: initialData?.location || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("企業名は必須です");
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <form
        className="bg-white rounded-xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onSubmit={handleSubmit}
      >
        <h2 className="m-0 mb-6 text-gray-800 text-center text-2xl">
          {isEditMode ? "企業情報編集" : "企業情報登録"}
        </h2>

        <div className="mb-6">
          <label
            htmlFor="name"
            className="block mb-2 font-medium text-gray-800"
          >
            企業名 *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="株式会社サンプル"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="industry"
            className="block mb-2 font-medium text-gray-800"
          >
            業界
          </label>
          <input
            type="text"
            id="industry"
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            placeholder="IT・ソフトウェア"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="website"
            className="block mb-2 font-medium text-gray-800"
          >
            ウェブサイト
          </label>
          <input
            type="url"
            id="website"
            name="website"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://example.com"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="mypage_url"
            className="block mb-2 font-medium text-gray-800"
          >
            採用マイページURL
          </label>
          <input
            type="url"
            id="mypage_url"
            name="mypage_url"
            value={formData.mypage_url}
            onChange={handleChange}
            placeholder="https://recruit.example.com"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="mypage_id"
            className="block mb-2 font-medium text-gray-800"
          >
            マイページログインID
          </label>
          <input
            type="text"
            id="mypage_id"
            name="mypage_id"
            value={formData.mypage_id}
            onChange={handleChange}
            placeholder="your-id@example.com"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="interest_level"
            className="block mb-2 font-medium text-gray-800"
          >
            志望度
          </label>
          <select
            id="interest_level"
            name="interest_level"
            value={formData.interest_level || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                interest_level: e.target.value
                  ? parseInt(e.target.value)
                  : undefined,
              })
            }
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          >
            <option value="">選択してください</option>
            <option value="1">★☆☆☆☆ (1)</option>
            <option value="2">★★☆☆☆ (2)</option>
            <option value="3">★★★☆☆ (3)</option>
            <option value="4">★★★★★☆ (4)</option>
            <option value="5">★★★★★ (5)</option>
          </select>
        </div>

        <div className="mb-6">
          <label
            htmlFor="salary_min"
            className="block mb-2 font-medium text-gray-800"
          >
            想定年収（最小）
          </label>
          <input
            type="number"
            id="salary_min"
            name="salary_min"
            value={formData.salary_min || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                salary_min:
                  e.target.value === "" ? null : parseInt(e.target.value),
              })
            }
            placeholder="400"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
          <p className="text-sm text-gray-500 mt-1">
            万円単位で入力してください
          </p>
        </div>

        <div className="mb-6">
          <label
            htmlFor="salary_max"
            className="block mb-2 font-medium text-gray-800"
          >
            想定年収（最大）
          </label>
          <input
            type="number"
            id="salary_max"
            name="salary_max"
            value={formData.salary_max || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                salary_max:
                  e.target.value === "" ? null : parseInt(e.target.value),
              })
            }
            placeholder="600"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
          <p className="text-sm text-gray-500 mt-1">
            万円単位で入力してください
          </p>
        </div>

        <div className="mb-6">
          <label
            htmlFor="salary_ave"
            className="block mb-2 font-medium text-gray-800"
          >
            平均年収
          </label>
          <input
            type="number"
            id="salary_ave"
            name="salary_ave"
            value={formData.salary_ave || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                salary_ave:
                  e.target.value === "" ? null : parseInt(e.target.value),
              })
            }
            placeholder="600"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
          <p className="text-sm text-gray-500 mt-1">
            万円単位で入力してください
          </p>
        </div>

        <div className="mb-6">
          <label
            htmlFor="holidays"
            className="block mb-2 font-medium text-gray-800"
          >
            年間休日数
          </label>
          <input
            type="number"
            id="holidays"
            name="holidays"
            value={formData.holidays || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                holidays:
                  e.target.value === "" ? null : parseInt(e.target.value),
              })
            }
            placeholder="120"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="employee_count"
            className="block mb-2 font-medium text-gray-800"
          >
            従業員数
          </label>
          <input
            type="number"
            id="employee_count"
            name="employee_count"
            value={formData.employee_count || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                employee_count:
                  e.target.value === "" ? null : parseInt(e.target.value),
              })
            }
            placeholder="500"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="location"
            className="block mb-2 font-medium text-gray-800"
          >
            勤務地
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="東京都渋谷区"
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="benefits"
            className="block mb-2 font-medium text-gray-800"
          >
            福利厚生
          </label>
          <textarea
            id="benefits"
            name="benefits"
            value={formData.benefits}
            onChange={(e) =>
              setFormData({
                ...formData,
                benefits: e.target.value,
              })
            }
            placeholder="住宅手当、交通費支給、社会保険完備など"
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
          />
        </div>

        <div className="flex gap-4 justify-end mt-8 flex-col md:flex-row">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-100 text-gray-600 border border-gray-300 py-3 px-6 rounded-lg text-base cursor-pointer transition-all hover:bg-gray-200"
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="bg-gradient-to-br from-blue-500 to-green-600 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            {isEditMode ? "更新" : "登録"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompaniesPage;
